import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'app/components/atoms/Icon/Icon';
import TypeIcon from 'app/components/atoms/TypeIcon/TypeIcon';
import Avatar from 'app/components/molecules/Avatar/Avatar';
import Loader from 'app/components/atoms/Loader/Loader';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import {
    SectionTitle,
    ResultsList,
    ResultItem,
    ResultIcon,
    ResultContent,
    ResultTitle,
    ResultMeta,
    ResultMetaItem,
    ResultType,
    NoResults,
    SearchIcon,
} from '../GlobalSearchModal.styles';

const EntityIcon = ({ entity, entityTypes }) => {
    if (entity.image) {
        const entityType = entity.type || entity.typename?.toLowerCase();
        const imageSrc = entity.id && entityType
            ? getAttachmentUrl(entity.id, entityType, entity.image)
            : entity.image;
        
        return (
            <Avatar
                src={imageSrc}
                name={entity.name}
                alt={entity.name}
                width="24px"
                height="24px"
                lineHeight="24px"
            />
        );
    }
    
    const entityTypeKey = (entity.type || entity.typename || '').toLowerCase();
    const typeDefinition = entityTypes.find((type) => type.uri === entityTypeKey);
    
    if (entity.iconName) {
        return (
            <Icon
                name={entity.iconName}
                type={entity.iconType}
                hexColor={entity.iconColor}
                size="md"
            />
        );
    }
    
    return (
        <TypeIcon
            type={entity?.type}
            hexColor={typeDefinition?.color}
            size="md"
        />
    );
}

const ResultRow = ({ result, entityTypes, onClick }) => {
    return (
        <ResultItem onClick={() => onClick(result)}>
            <ResultIcon>
                <EntityIcon entity={result} entityTypes={entityTypes} />
            </ResultIcon>
            
            <ResultContent>
                <ResultTitle>{result.name || 'Unnamed'}</ResultTitle>
                
                <ResultMeta>
                    {(result.type || result.category) && (
                        <ResultType>{result.type || result.category}</ResultType>
                    )}
                    {result.id && (
                        <ResultMetaItem>#{result.id.slice(0, 8)}</ResultMetaItem>
                    )}
                </ResultMeta>
            </ResultContent>
        </ResultItem>
    );
}

const SearchResults = ({
    results,
    totalCount,
    isLoadingMore,
    entityTypes,
    onResultClick,
    onScroll,
    resultsRef,
}) => {
    return (
        <>
            <SectionTitle variant="caption">
                {totalCount} {totalCount === 1 ? 'result' : 'results'} found
            </SectionTitle>
            <ResultsList ref={resultsRef} onScroll={onScroll}>
                {results.map((result, index) => (
                    <ResultRow
                        key={result.id || index}
                        result={result}
                        entityTypes={entityTypes}
                        onClick={onResultClick}
                    />
                ))}
                {isLoadingMore && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                        <Loader size={24} />
                    </div>
                )}
            </ResultsList>
        </>
    );
}

SearchResults.propTypes = {
    results: PropTypes.array.isRequired,
    totalCount: PropTypes.number.isRequired,
    isLoadingMore: PropTypes.bool,
    entityTypes: PropTypes.array,
    onResultClick: PropTypes.func.isRequired,
    onScroll: PropTypes.func,
    resultsRef: PropTypes.func,
};

SearchResults.defaultProps = {
    entityTypes: [],
    isLoadingMore: false,
};

const NoSearchResults = ({ searchSummary }) => {
    return (
        <NoResults>
            <SearchIcon name="magnify" size="xl" />
            <p>No results found</p>
        </NoResults>
    );
}

NoSearchResults.propTypes = {
    searchSummary: PropTypes.string,
};

export { SearchResults, NoSearchResults };

